import React, { useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { loginUser } from "../loginSlice";
import "./login.css";
import { setBackButtonUrl } from "../uiSlice";
import Swal from "sweetalert2";

function Login() {
  const dispatch = useDispatch();

  const { status, user, error } = useSelector((state) => state.auth);

  const { status: themeStatus, data: themeData } = useSelector(
    (state) => state.theme
  );

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const isAdmin = searchParams.get("admin") === "true";

  const fromMobileAppQuery =
    String(searchParams.get("fromMobileApp") || "")
      .trim()
      .toLowerCase() === "true";

  const loginDispatchedRef = useRef(false);

  // Persist redirect and mobile flag
  const backButtonRedirectRef = useRef(null);
  const fromMobileAppRef = useRef(false);

  // ============================================================
  // LOGIN
  // ============================================================
  useEffect(() => {
    if (loginDispatchedRef.current) return;

    loginDispatchedRef.current = true;

    // Capture mobile flag immediately
    fromMobileAppRef.current = fromMobileAppQuery;

    dispatch(loginUser());
  }, [dispatch, fromMobileAppQuery]);

  // ============================================================
  // CAPTURE USER REDIRECT / MOBILE FLAG
  // ============================================================
  useEffect(() => {
    if (user?.backButtonRedirect) {
      backButtonRedirectRef.current = user.backButtonRedirect;
    }

    if (user?.fromMobileApp) {
      fromMobileAppRef.current = true;
    }
  }, [user]);

  // ============================================================
  // ADMIN REDIRECT
  // ============================================================
  useEffect(() => {
    if (status === "succeeded" && user && isAdmin) {
      navigate("/admin");
    }
  }, [status, user, isAdmin, navigate]);

  // ============================================================
  // SAVE BACK BUTTON URL
  // ============================================================
  useEffect(() => {
    if (status === "succeeded" && user?.backButtonRedirect) {
      dispatch(setBackButtonUrl(user.backButtonRedirect));
    }
  }, [status, user, dispatch]);

  // ============================================================
  // SAFE REDIRECT
  // ============================================================
  const safeRedirect = (raw) => {
    if (!raw) {
      return "/";
    }

    let redirect = String(raw).trim();

    // Handle null / undefined / empty values
    if (
      redirect === "" ||
      redirect.toLowerCase() === "null" ||
      redirect.toLowerCase() === "undefined"
    ) {
      return "/";
    }



    const httpsIndex = redirect.indexOf("https://", 1);
    const httpIndex = redirect.indexOf("http://", 1);

    if (httpsIndex !== -1) {
      redirect = redirect.substring(httpsIndex);
    } else if (httpIndex !== -1) {
      redirect = redirect.substring(httpIndex);
    }

    return redirect;
  };

  // ============================================================
  // REDIRECT HANDLER
  // ============================================================
  const goToRedirect = (target) => {
    const redirect = safeRedirect(target);

    console.log("[Login] Original redirect:", target);
    console.log("[Login] Clean redirect:", redirect);

    if (/^https?:\/\//i.test(redirect)) {
      // External URL
      window.location.assign(redirect);
    } else {
      // Internal React route
      navigate(redirect);
    }
  };

  // ============================================================
  // SESSION FAILED
  // ============================================================
  useEffect(() => {
    if (status !== "failed") return;

    const msg = error ? String(error) : "Session expired";

    // Mobile app handles its own redirect
    if (fromMobileAppRef.current) {
      console.log(
        "[Login] Mobile app session failed, skipping redirect",
        { msg }
      );

      return;
    }

    const redirect = safeRedirect(backButtonRedirectRef.current);

    console.log("[Login] Session failed redirect:", redirect);

    Swal.fire("Session Expired!", msg, "error").then(() => {
      goToRedirect(redirect);
    });
  }, [status, error]);

  // ============================================================
  // ALREADY PLAYED / GAMEOVER
  // ============================================================
  useEffect(() => {
    if (isAdmin) return;

    if (status !== "succeeded" || !user) return;

    if (Number(user.gameover ?? 0) > 0) {
      // Mobile app handles already played state
      if (fromMobileAppRef.current) {
        console.log(
          "[Login] Mobile: gameover detected, skipping redirect"
        );

        return;
      }

      const redirect = safeRedirect(user.backButtonRedirect);

      console.log("[Login] Gameover original redirect:", user.backButtonRedirect);
      console.log("[Login] Gameover clean redirect:", redirect);

      void Swal.fire(
        "You have already played!",
        " ",
        "error"
      ).then(() => {
        goToRedirect(redirect);
      });
    }
  }, [status, user, isAdmin]);

  // ============================================================
  // LOADER
  // ============================================================
  if (status === "loading" || themeStatus === "loading") {
    return (
      <div className="login-main-container">
        <div className="quiz-loader-container">
          <div className="quiz-loader"></div>
        </div>
      </div>
    );
  }

  // ============================================================
  // LOGIN PAGE
  // ============================================================
  return (
    <div className="login-main-container">
      {themeData && (
        <img
          src={process.env.REACT_APP_S3_PATH + themeData.logo}
          className="login-logo"
          alt="Game Logo"
        />
      )}

      {user && status === "succeeded" && (
        <div>
          <Link
            to="/rules"
            className="begin-play-btn"
            style={{
              ...(themeData?.box_bg_color && {
                backgroundColor: themeData.box_bg_color,
              }),

              ...(!themeData?.box_bg_color &&
                themeData?.button_color && {
                  backgroundColor: themeData.button_color,
                }),

              ...(themeData?.box_text_color && {
                color: themeData.box_text_color,
              }),

              ...(!themeData?.box_text_color &&
                themeData?.button_text_color && {
                  color: themeData.button_text_color,
                }),
            }}
          >
            BEGIN PLAY
          </Link>
        </div>
      )}
    </div>
  );
}

export default Login;